import { useEffect, useState } from "react";
import * as Yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import { useReactHookForm } from "../../hooks/useReactHookForm";
import { useAuthorize } from "../../hooks/use-authorize";
import { UserPermissionName } from "../../models/user-permission-name";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, Divider, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { ReactHookForm } from "../../components/hook-form/react-hook-form.component";
import { unitsApiKeyStore } from "../../api/units-api-key-store";
import { LoadingIndicator } from "../../components/loading-indicator.component";
import { FormSingleCheckboxAvatar } from "../../components/hook-form/form-single-checkbox-avatar.component";
import { FormMultiCheckboxAvatar } from "../../components/hook-form/form-checkbox-sngle-avatar.component";
import FormInput from "../query-builder/hooks-form-querybuilder/form-input.component";
import { useLoadData } from "../../hooks/useLoadData";
import { ApiResponse } from "../../models/api-response";
import { useSnackbar } from "../../hooks/use-snackbar.hook";

const emailValidationSchema = Yup.object().shape({
  contacts: Yup.array().required('Contacts is required'),
});


interface CocEmailConfirmDialogProps {
  open: boolean;
  title?: string;
  message: string;
  unitID?: any;
  onClose: () => void;
  onConfirm: (data: any) => void;
  isLoading?: boolean;
  autoCloseDuration?: number;
  hideButtons?: boolean;
}

const CocEmailConfirmDialog = ({
  open,
  title,
  message,
  unitID,
  onClose,
  onConfirm,
  isLoading = false,
  autoCloseDuration,
  hideButtons = false,
}: CocEmailConfirmDialogProps) => {
  useEffect(() => {
    if (open && autoCloseDuration) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDuration);
      return () => clearTimeout(timer);
    }
  }, [open, autoCloseDuration, onClose]);

  const { showSnackbar } = useSnackbar();

  const form = useReactHookForm({
    defaultValues: {
      sendReport: false,
      additionalEmail: "",
      contacts: []
    },
    // yupSchema: isChecked && emailValidationSchema
  });

  const [contacts, setContacts] = useState<any[]>([])
  const [municipalityEmail, setMunicipalityEmail] = useState<any>("");
  const [municipalityPhone, setMunicipalityPhone] = useState<any>("");

  const { data, isLoading: isUnitEmailContactLoading, isFetching } = useLoadData<ApiResponse<any[]>>(
    unitsApiKeyStore.getEmailContactsForUnit(unitID)
  );

  useEffect(() => {
    if (data) {
      const responseData: any = data?.data;
      setContacts(responseData?.contacts);
      setMunicipalityEmail(responseData?.municipalityEmail);
      setMunicipalityPhone(responseData?.municipalityPhone);

    }
  }, [data])

  const handleSubmit = (dialodData: any) => {
    if (!dialodData?.sendReport && dialodData?.contacts?.length == 0 && (dialodData?.additionalEmail == "")) {
      showSnackbar(
        'Please select either municipality or contact, or provide a value in the text field.',
        'error'
      );
      return;
    }
    const payload = {
      unitID: unitID,
      isSendtoMuniContact: dialodData?.sendReport,
      contactIDs: dialodData?.contacts,
      ccEmailAddresses: dialodData?.additionalEmail ?? null,
    }
    onConfirm(payload);
  };


  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={open}
      className="modalpopup"
      title={title}
    // onClose={onClose}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 10,
          top: 13,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <ReactHookForm formName="COC_REPORT_FORM" onSubmit={handleSubmit} formContextProps={form}>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xl={12} lg={12} sm={12} xs={12}>
              <Typography variant="subtitle1">{message}</Typography>
            </Grid>
            <Grid item xl={12} lg={12} sm={12} xs={12}><Divider /></Grid>
            <Grid item xl={12} lg={12} sm={12} xs={12}>
              <Grid
                container
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <FormSingleCheckboxAvatar
                    name="sendReport"
                    label="Municipality"
                    email={municipalityEmail}
                    phone={municipalityPhone} />
                </div>
                <Typography
                  variant="body1"
                  style={{ padding: "3px 10px", backgroundColor: '#DBEAFE' }}>Primary</Typography>
              </Grid>
            </Grid>
            <Grid item xl={12} lg={12} sm={12} xs={12}><Divider /></Grid>
            <Grid item xl={12} lg={12} sm={12} xs={12}>
              <Grid container spacing={1}>
                <Grid item xl={12} lg={12} sm={12} xs={12}>
                  <Typography variant='body1'><b>ADDITIONAL CONTACTS</b></Typography>
                </Grid>
                <Grid item xl={12} lg={12} sm={12} xs={12}>
                  <FormMultiCheckboxAvatar
                    name="contacts"
                    valueKey="contactsID"
                    label="contactsFullName"
                    email="contactsWorkEmail"
                    phone="contactsCellPhone"
                    dataItems={contacts}
                    isParentLoading={isUnitEmailContactLoading || isFetching}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xl={12} lg={12} sm={12} xs={12}><Divider /></Grid>
            <Grid item xl={12} lg={12} sm={12} xs={12}>
              <Grid container spacing={1}>
                <Grid item xl={12} lg={12} sm={12} xs={12}>
                  <Typography variant='body1'><b>Ad hoc - additional email</b></Typography>
                </Grid>
                <Grid item xl={12} lg={12} sm={12} xs={12}>
                  <FormInput
                    label="Ad hoc - additional email"
                    name="additionalEmail"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <LoadingIndicator isLoading={isLoading} />
        </DialogContent>
        <Divider />
        {!hideButtons && (
          <DialogActions>
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Button type="button"
                size="small"
                variant="contained"
                onClick={onClose}
                disableElevation
                style={{ backgroundColor: '#d7dbdd', color: 'black' }}>
                Cancel
              </Button>
              <Button type="submit"
                size="small"
                variant="contained"
                disableElevation
                disabled={isLoading || isReadOnlyPermission}>
                Yes
              </Button>
            </Grid>
          </DialogActions>
        )}
      </ReactHookForm>
    </Dialog>
  );
};

export default CocEmailConfirmDialog;
